import { isSystemTag } from '@tunasong/models'
import type { RenderElementPropsExt } from '@tunasong/plugin-lib'
import { useEditor } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { useDeviceUISettings, useMergeRefs } from '@tunasong/ui-lib'
import cn from 'classnames'
import { useEffect, useRef, useState, type CSSProperties, type FC, type RefObject } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useIsInSelectionRange } from '../hooks/in-selection-range.js'
import { useNodeSelected } from '../hooks/use-node-selected.js'
import { BlockDecorationsBar } from './block-decorations-bar.js'
import { BlockGutter } from './block-gutter.js'
import styles from './core-block.module.css'
import { HiddenBlock } from './hidden-block.js'
import { useAnchoredState } from './use-anchored-state.js'

export interface ConfigureBlock {
  prop: string
  validate: (val: unknown) => boolean
  type?: 'text'

  title?: string
}

/** DOM event handlers that block consumers may need to attach */
type BlockEventHandlers = Pick<
  React.DOMAttributes<HTMLElement>,
  'onClick' | 'onDoubleClick' | 'onMouseDown' | 'onMouseUp' | 'onContextMenu' | 'onKeyDown' | 'onKeyUp'
>

export interface CoreBlockProps extends RenderElementPropsExt, BlockEventHandlers {
  className?: string
  title?: React.ReactNode
  style?: CSSProperties
  component?: 'div' | 'span'
  element: CoreElement
  focus?: boolean
  /** Right border on focus */
  border?: boolean | 'right'
  menu?: boolean
  /** Which side to place the gutter (menu + label). Default: 'left' */
  gutterSide?: 'left' | 'right'

  label?: string

  /** Internal */
  dragRef?: DragElementWrapper<DragSourceOptions>
  blockRef?: RefObject<HTMLDivElement | null>
  dropLine?: 'bottom' | 'left' | 'right' | 'top' | ''
  isDragOver?: boolean
}

/**
 * Single wrapper for blocks that need selection awareness (focus or menu).
 * Calls useNodeSelected() once and provides both focus styling and gutter hasFocus.
 */
const SelectionAwareBlock: FC<{
  focus: boolean
  menu: boolean
  border: boolean | 'right'
  isDragOver?: boolean
  isInline: boolean
  editor: ReturnType<typeof useEditor>
  element: CoreElement
  label?: string
  gutterSide?: 'left' | 'right'
  dragRef?: DragElementWrapper<DragSourceOptions>
  children: (blockClassName: string, gutter: React.ReactNode) => React.ReactNode
}> = ({
  focus,
  menu,
  border,
  isDragOver,
  isInline,
  editor,
  element,
  label,
  gutterSide = 'left',
  dragRef,
  children,
}) => {
  const blockSelected = useNodeSelected(element)
  const hasFocus = focus && blockSelected
  const hasBorder = border === true
  const hasFocusBorder = hasFocus && border === true
  const hasFocusRightBorder = hasFocus && border === 'right'

  const blockClassName = cn(styles.block, {
    [styles['block--inline']]: isInline,
    [styles['block--border']]: hasBorder,
    [styles['block--focused']]: hasFocusBorder,
    [styles['block--focusRight']]: hasFocusRightBorder,
    [styles['block--dragOver']]: isDragOver,
  })

  const gutter = menu ? (
    <BlockGutter
      editor={editor}
      element={element}
      blockName={`${element.type[0].toUpperCase()}${element.type.slice(1)} Block`}
      hasFocus={hasFocus}
      label={label}
      side={gutterSide}
      dragRef={dragRef}
    />
  ) : null

  return <>{children(blockClassName, gutter)}</>
}

/** A Block rendered as a plain element with CSS variables for theme values */
export const CoreBlock: FC<CoreBlockProps> = props => {
  const {
    className,
    style,
    component: Component = 'div',
    focus = true,
    menu = false,
    border = 'right',
    gutterSide,
    attributes,
    element,
    label,
    children,
    /** DnD props */
    blockRef,
    dragRef,
    dropLine,
    isDragOver,
    /** DOM event handlers */
    onClick,
    onDoubleClick,
    onMouseDown,
    onMouseUp,
    onContextMenu,
    onKeyDown,
    onKeyUp,
  } = props

  const editor = useEditor()

  const { anchored, active, hovered } = useAnchoredState(element.id)

  // One-shot spotlight pulse on the false→true transition of `active`. The
  // CSS keyframe runs for 600ms then we clear the attribute so a re-activation
  // can retrigger it. Reduced-motion users see no animation regardless — the
  // gate is in CSS, not here, so the data attribute is set unconditionally.
  const [pulse, setPulse] = useState(false)
  const prevActive = useRef(false)
  useEffect(() => {
    if (active && !prevActive.current) {
      setPulse(true)
      const timer = window.setTimeout(() => setPulse(false), 600)
      prevActive.current = true
      return () => window.clearTimeout(timer)
    }
    prevActive.current = active
    return undefined
  }, [active])

  const tagsFilter = useDeviceUISettings(state => state.tagsFilter)

  const multiRootRef = useMergeRefs(attributes.ref, blockRef)

  const isInline = Component === 'span'
  const needsSelection = focus || menu

  /** Block-level range-selection feedback — paints a contiguous bar through interior blocks
   * of multi-block selections so empty lines and chord rows remain visible/continuous. */
  const inSelectionRange = useIsInSelectionRange(element)

  // Hide all tagged blocks (non-system tags) not matching filter if filter is set
  const hiddenByTagsFilter =
    tagsFilter.length > 0 && element.tags?.some(tag => !isSystemTag(tag) && !tagsFilter.includes(tag)) === true

  if (hiddenByTagsFilter) {
    return (
      <HiddenBlock contentEditable={false} text="Hidden by tags filter" attributes={attributes}>
        {children}
      </HiddenBlock>
    )
  }

  const InnerTag = isInline ? 'span' : 'div'

  const renderBlock = (blockClassName: string, gutter?: React.ReactNode) => (
    <Component {...attributes} ref={multiRootRef} className={styles.wrapper}>
      {dropLine === 'top' && <div className={styles.dropLine} contentEditable={false} />}
      <InnerTag
        className={cn(blockClassName, className)}
        data-element-id={element.id}
        data-in-range={inSelectionRange ? 'true' : undefined}
        // Personal-note anchor highlight states. The CSS in `core-block.module.css`
        // is keyed entirely on these attributes (see the cascade
        // `[data-anchored] ≺ [data-anchored-hover] ≺ [data-anchored-active]`),
        // so without writing them here the styles — and the marker's
        // `--tuna-anchor-active` interpolation — never engage.
        // `undefined` removes the attribute entirely so the attribute selector
        // doesn't accidentally match `data-anchored=""`.
        data-anchored={anchored ? 'true' : undefined}
        data-anchored-hover={hovered ? 'true' : undefined}
        data-anchored-active={active ? 'true' : undefined}
        data-anchored-pulse={pulse ? 'true' : undefined}
        style={style}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onContextMenu={onContextMenu}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      >
        {gutter}
        <BlockDecorationsBar editor={editor} element={element} />
        <>{children}</>
      </InnerTag>
      {dropLine === 'bottom' && <div className={styles.dropLine} contentEditable={false} />}
    </Component>
  )

  if (needsSelection) {
    return (
      <SelectionAwareBlock
        focus={focus}
        menu={menu}
        border={border}
        isDragOver={isDragOver}
        isInline={isInline}
        editor={editor}
        element={element}
        label={label}
        gutterSide={gutterSide}
        dragRef={dragRef}
      >
        {renderBlock}
      </SelectionAwareBlock>
    )
  }

  const hasBorder = border === true
  const simpleClassName = cn(styles.block, {
    [styles['block--inline']]: isInline,
    [styles['block--border']]: hasBorder,
  })

  return renderBlock(simpleClassName)
}
